/**
 * useProductivityStats Hook
 * Computes daily, weekly, and monthly productivity metrics from tasks data.
 */
import { useMemo } from 'react';
import { getLocalDateString, QUADRANT_XP } from './useGamification';

export function getStartOfWeek(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday is start
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function computeProductivityStats(quadrants = []) {
  const todayStr = getLocalDateString();
  const allTasks = [];

  quadrants.forEach((q) => {
    (q.tasks || []).forEach((t) => {
      allTasks.push({
        ...t,
        quadrantId: q.id,
        quadrantTitle: q.title,
        color: q.color,
      });
    });
  });

  const completedTasks = allTasks.filter((t) => t.completed);

  // 1. Daily Stats
  const todayCompletedTasks = completedTasks.filter(
    (t) => t.completedAt && t.completedAt.startsWith(todayStr)
  );

  const todayPlannedTasks = allTasks.filter(
    (t) => t.plannedDate === todayStr || (t.completedAt && t.completedAt.startsWith(todayStr))
  );

  const todayXP = todayCompletedTasks.reduce((acc, t) => {
    return acc + (QUADRANT_XP[t.quadrantId] ?? 30);
  }, 0);

  // 2. Weekly Stats (Last 7 days: Monday to Sunday)
  const monday = getStartOfWeek();
  const dayLabels = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
  const weeklyDays = [];

  for (let i = 0; i < 7; i++) {
    const curDate = new Date(monday);
    curDate.setDate(monday.getDate() + i);
    const dateStr = getLocalDateString(curDate);

    const count = completedTasks.filter(
      (t) => t.completedAt && t.completedAt.startsWith(dateStr)
    ).length;

    weeklyDays.push({
      date: dateStr,
      label: dayLabels[i],
      count,
      isToday: dateStr === todayStr,
    });
  }

  // Quadrant breakdown for completed tasks
  const quadrantBreakdown = { 0: 0, 1: 0, 2: 0, 3: 0 };
  completedTasks.forEach((t) => {
    if (quadrantBreakdown[t.quadrantId] !== undefined) {
      quadrantBreakdown[t.quadrantId] += 1;
    }
  });

  // 3. Monthly Heatmap Stats (Past 28 days - 4 weeks grid)
  const monthlyDays = [];
  const now = new Date();
  for (let i = 27; i >= 0; i--) {
    const curDate = new Date(now);
    curDate.setDate(now.getDate() - i);
    const dateStr = getLocalDateString(curDate);

    const count = completedTasks.filter(
      (t) => t.completedAt && t.completedAt.startsWith(dateStr)
    ).length;

    // Intensity level: 0 (0), 1 (1-2), 2 (3-4), 3 (5+)
    let level = 0;
    if (count >= 5) level = 3;
    else if (count >= 3) level = 2;
    else if (count >= 1) level = 1;

    monthlyDays.push({
      date: dateStr,
      count,
      level,
      isToday: dateStr === todayStr,
    });
  }

  const thisMonthStr = todayStr.slice(0, 7); // YYYY-MM
  const monthCompletedCount = completedTasks.filter(
    (t) => t.completedAt && t.completedAt.startsWith(thisMonthStr)
  ).length;

  return {
    todayStr,
    todayCompletedCount: todayCompletedTasks.length,
    todayPlannedCount: todayPlannedTasks.length,
    todayXP,
    todayCompletedTasks,
    weeklyDays,
    weeklyTotal: weeklyDays.reduce((acc, d) => acc + d.count, 0),
    quadrantBreakdown,
    monthlyDays,
    monthCompletedCount,
    totalCompletedAllTime: completedTasks.length,
  };
}

export function useProductivityStats(quadrants) {
  return useMemo(() => computeProductivityStats(quadrants), [quadrants]);
}
